import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import { Body5, ConfirmModal } from '@ui';
import { FlatButton, SkeletonButton } from '@ui/buttons';
import { showSuccessBanner, showErrorBanner } from '@utils/application';
import { ROUTES } from '@constants/routes';
import { scoreBackfillPathForSpec } from '@features/scoreBackfill/specSearch';
import { lockSpec, approveSpec, enableLive, disableLive, deleteOrRetireSpec } from '@services/scoreSpecs';
import { listFeeds } from '@services/feeds';
import { feedNamesFor } from './summary';

// Confirm descriptors keyed by action. `service(id)` performs the transition; `ok` is the success
// banner text. delete/retire share deleteOrRetireSpec and are handled specially in runConfirm.
const CONFIRMS = {
  lock: {
    title: 'Lock this draft?',
    body: 'Locking freezes the definition — you won’t be able to edit it after.',
    confirmLabel: 'Lock draft',
    ok: 'Spec locked',
    service: lockSpec,
  },
  approve: {
    title: 'Approve this spec?',
    body: 'Approval makes it eligible for backfill and for live firing.',
    confirmLabel: 'Approve',
    ok: 'Spec approved',
    service: approveSpec,
  },
  enable_live: {
    title: 'Enable live firing?',
    body: 'This spec will start scoring newly-ingested documents. Fleet propagation is still rolling out, so the effect may lag.',
    confirmLabel: 'Enable live firing',
    ok: 'Live firing on',
    service: enableLive,
  },
  delete: {
    title: 'Permanently delete this draft?',
    body: 'This can’t be undone.',
    confirmLabel: 'Delete draft',
    service: deleteOrRetireSpec,
  },
  retire: {
    title: 'Retire this spec?',
    body: 'It’s kept for audit, live firing is turned off, and it can’t be edited or re-approved.',
    confirmLabel: 'Retire',
    service: deleteOrRetireSpec,
  },
};

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

// Irreversible actions sit below a divider, set visually apart from routine management.
const RetireBlock = styled.div`
  border-top: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  padding-top: 16px;
`;

const GroupTitle = styled.div`
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-weight: 700;
  margin-bottom: 8px;
  color: ${({ theme, $danger }) => ($danger ? theme.themeColors.negative : theme.themeColors.secondaryText)};
`;

const Caption = styled.div`
  font-size: 12px;
  line-height: 1.4;
  max-width: 64ch;
  color: ${({ theme, $danger }) => ($danger ? theme.themeColors.negative : theme.themeColors.secondaryText)};
`;

const ActionItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;

  & + & {
    margin-top: 12px;
  }
`;

const NextButton = styled.div`
  margin-top: 10px;
`;

// FlatButton styling, recoloured to the destructive token. Overrides the nested BaseButton fill
// (FlatButtonWrap hardcodes primaryCtaButton) with !important so it beats that same-specificity rule.
const DangerFlatButton = styled.div`
  button {
    background: ${({ theme }) => theme.themeColors.negative} !important;
    border-color: ${({ theme }) => theme.themeColors.negative} !important;
  }
`;

// The feed-name pre-check runs at LOCK, not at approve, and that is the whole point of it.
//
// Feed-name uniqueness is enforced only across the APPROVED set and only at approve time — a draft
// reserves nothing, which is deliberate: you can author v4 reusing v3's feed_name and retire v3 as
// part of the cutover. But there is no unlock endpoint. A spec that 409s at approve is already
// frozen, and its only remaining move is to be retired and re-created. So the last moment the
// warning is still actionable is before the Lock button, and that is where it goes.
//
// It is ADVISORY and never gates the button, for two reasons: /feeds is a different grant from the
// score-spec API (an operator may legitimately get a 403 here), and the collision may be exactly
// what the operator intends — reusing v3's name and retiring v3 next.
const FEED_CHECK = { CHECKING: 'checking', DONE: 'done', UNAVAILABLE: 'unavailable' };

const list = (items) => items.join(', ');

// The status-driven action bar. Which controls appear is a pure function of status + live_enabled,
// organized into labelled blocks (Next step / Manage / Retire or remove) so the shape of the panel
// teaches the lifecycle. Buttons guard on `busy` because @ui buttons are visual-only-disabled.
export const SpecLifecycleBar = ({
  spec,
  onChanged,
  onDuplicate = () => {},
  problemCount = 0,
  saveBlockerCount = 0,
  isDirty = false,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [confirmKey, setConfirmKey] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [feedCheck, setFeedCheck] = React.useState(null);

  const id = spec.score_spec_id;
  const { status } = spec;
  const live = spec.live_enabled;

  // The feed names the SAVED definition would publish. spec_json, not the editor buffer: lock
  // freezes what is stored.
  const plannedFeeds = React.useMemo(() => feedNamesFor(spec.spec_json), [spec.spec_json]);

  const cancelRef = React.useRef({ isCanceled: false });
  React.useEffect(() => {
    const token = cancelRef.current;
    return () => {
      token.isCanceled = true;
    };
  }, []);

  const openLock = async () => {
    setConfirmKey('lock');
    if (plannedFeeds.length === 0) return;
    setFeedCheck({ state: FEED_CHECK.CHECKING, collisions: [] });
    const { feeds, error } = await listFeeds();
    if (cancelRef.current.isCanceled) return;
    if (error) {
      setFeedCheck({ state: FEED_CHECK.UNAVAILABLE, collisions: [] });
      return;
    }
    setFeedCheck({
      state: FEED_CHECK.DONE,
      collisions: plannedFeeds.filter((feed) => (feeds || []).indexOf(feed) > -1),
    });
  };

  // One paragraph, because ConfirmModal renders its body as a single text node.
  const lockBody = () => {
    const base = CONFIRMS.lock.body;
    if (!feedCheck) return base;
    if (feedCheck.state === FEED_CHECK.CHECKING) return `${base} Checking whether its feed names are already taken…`;
    if (feedCheck.state === FEED_CHECK.UNAVAILABLE) {
      return `${base} The published feed list could not be read, so whether ${list(
        plannedFeeds
      )} is already taken is unknown — this account may not have feed access. If one of them is, approve returns 409 and a locked spec can only be retired, not unlocked.`;
    }
    if (feedCheck.collisions.length > 0) {
      return `${base} ${list(feedCheck.collisions)} ${
        feedCheck.collisions.length === 1 ? 'is' : 'are'
      } ALREADY PUBLISHED by an approved spec. Approve will return 409, and a locked spec cannot be unlocked — only retired and re-created. Either change the feed name first, or be sure you are retiring the spec that owns it as part of this cutover.`;
    }
    return `${base} Its feed names (${list(plannedFeeds)}) are not published yet.`;
  };

  const runConfirm = async () => {
    const cfg = CONFIRMS[confirmKey];
    if (!cfg || busy) return;
    setBusy(true);
    const res = await cfg.service(id);
    setBusy(false);
    setConfirmKey(null);
    setFeedCheck(null);

    if (confirmKey === 'delete' || confirmKey === 'retire') {
      if (res.deleted) {
        showSuccessBanner('Draft deleted', null, dispatch);
        navigate(ROUTES.scoreSpec);
      } else if (res.spec) {
        showSuccessBanner('Spec retired', null, dispatch);
        onChanged(res.spec);
      } else {
        showErrorBanner(res.error, null, dispatch);
      }
      return;
    }

    if (res.spec) {
      showSuccessBanner(cfg.ok, null, dispatch);
      onChanged(res.spec);
    } else {
      showErrorBanner(res.error, null, dispatch); // e.g. lock 422 = won't compile
    }
  };

  const onDisableLive = async () => {
    if (busy) return;
    setBusy(true);
    const { spec: next, error } = await disableLive(id);
    setBusy(false);
    if (next) {
      showSuccessBanner('Live firing off', null, dispatch);
      onChanged(next);
    } else {
      showErrorBanner(error, null, dispatch);
    }
  };

  const cfg = confirmKey ? CONFIRMS[confirmKey] : null;
  const showRetireBlock = status === 'draft' || status === 'locked' || status === 'approved';

  return (
    <Wrap>
      <div>
        {status === 'draft' && (
          <>
            <Caption>
              Freeze the definition so it can be reviewed. You won’t be able to edit it after locking — and it must be
              locked before it can be approved.
            </Caption>
            {isDirty && (
              <Caption $danger>
                There are unsaved edits. Locking freezes the SAVED definition, not what is on screen — save first, or
                the version you are looking at is not the one that gets frozen.
              </Caption>
            )}
            {problemCount > 0 && (
              <Caption $danger>
                {`${problemCount} thing${
                  problemCount === 1 ? '' : 's'
                } in this draft would be rejected by the server. You can still lock — these checks are a copy of the engine’s rules and copies drift — but the lock will be rejected if they are right.`}
              </Caption>
            )}
            {saveBlockerCount > 0 && (
              <Caption $danger>
                {`${saveBlockerCount} of those fail on SAVE, not at lock, so the version you are looking at is not in the store yet. Fix those first — locking freezes what was last saved.`}
              </Caption>
            )}
            <NextButton>
              {/* Deliberately never disabled. These checks mirror an allowlist the backend edits;
                  the day it enables a new kind, refusing to lock would strand a legal spec forever. */}
              <FlatButton onClick={openLock}>
                {problemCount > 0 ? `Lock anyway (${problemCount})` : 'Lock draft'}
              </FlatButton>
            </NextButton>
          </>
        )}
        {status === 'locked' && (
          <>
            <Caption>
              Mark this spec reviewed and ready. Approval makes it eligible for backfills and live firing, and can’t be
              undone from here.
            </Caption>
            <NextButton>
              <FlatButton onClick={() => setConfirmKey('approve')}>Approve</FlatButton>
            </NextButton>
          </>
        )}
        {status === 'approved' && !live && (
          <ActionItem>
            <FlatButton onClick={() => setConfirmKey('enable_live')}>Enable live firing</FlatButton>
            <Caption>Start scoring newly-ingested documents with this spec.</Caption>
          </ActionItem>
        )}
        {status === 'approved' && live && (
          <Body5 isLowContrast>No required next step — this spec is live and scoring. Manage it below.</Body5>
        )}
        {status === 'retired' && <Body5 isLowContrast>No further steps. Retired specs are read-only.</Body5>}
      </div>

      <div>
        {status === 'approved' && live && (
          <ActionItem>
            <SkeletonButton onClick={onDisableLive}>Turn off live firing</SkeletonButton>
            <Caption>
              Stop scoring new documents. Safe and reversible — applied immediately, no confirmation, and you can
              re-enable any time.
            </Caption>
          </ActionItem>
        )}
        {status === 'approved' && (
          <ActionItem>
            <SkeletonButton onClick={() => navigate(scoreBackfillPathForSpec(id))}>Run a backfill →</SkeletonButton>
            <Caption>
              Score a set of historical documents with this spec in a one-off run. Opens the backfill workspace with
              this spec selected.
            </Caption>
          </ActionItem>
        )}
        <ActionItem>
          <SkeletonButton onClick={onDuplicate}>Duplicate as next version</SkeletonButton>
          <Caption>
            Open a new editable draft seeded from this definition with the version bumped. Doesn’t change this spec.
          </Caption>
        </ActionItem>
      </div>

      {showRetireBlock && (
        <RetireBlock>
          <GroupTitle $danger>Remove this spec</GroupTitle>
          {status === 'draft' && (
            <ActionItem>
              <DangerFlatButton>
                <FlatButton onClick={() => setConfirmKey('delete')}>Delete draft</FlatButton>
              </DangerFlatButton>
              <Caption $danger>Permanently discard this draft. This can’t be undone.</Caption>
            </ActionItem>
          )}
          {(status === 'locked' || status === 'approved') && (
            <ActionItem>
              <DangerFlatButton>
                <FlatButton onClick={() => setConfirmKey('retire')}>Retire</FlatButton>
              </DangerFlatButton>
              <Caption $danger>
                Take this spec out of service. Live firing is turned off, it’s kept for audit, and it can’t be edited or
                re-approved. Can’t be undone.
              </Caption>
            </ActionItem>
          )}
        </RetireBlock>
      )}

      {cfg && (
        <ConfirmModal
          title={cfg.title}
          body={confirmKey === 'lock' ? lockBody() : cfg.body}
          confirmLabel={cfg.confirmLabel}
          busy={busy}
          onConfirm={runConfirm}
          onCancel={() => {
            if (busy) return;
            setConfirmKey(null);
            setFeedCheck(null);
          }}
        />
      )}
    </Wrap>
  );
};
